import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const EMPTY = { name: "", company: "", work_email: "", title: "", team_size: "", what_they_sell: "", note: "" };

export const DemoRequestDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.company || !form.work_email) return toast.error("Name, company and work email are required");
    setBusy(true);
    try {
      await api.post("/demo-request", form);
      setDone(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not submit request");
    } finally { setBusy(false); }
  };

  const close = (v) => { onOpenChange(v); if (!v) { setForm(EMPTY); setDone(false); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="demo-request-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="demo-thank-you">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-display text-xl font-semibold">Thanks - we'll be in touch</h3>
            <p className="text-sm text-muted-foreground mt-2">We will reply from DealsOps to schedule your demo. Use your work email.</p>
            <Button className="rounded-md mt-5" onClick={() => close(false)} data-testid="demo-close-btn">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Request a demo</DialogTitle>
              <DialogDescription>We will reply from DealsOps. Use your work email. There is no free plan or trial - demos are scheduled.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="demo-name" /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={set("company")} className="mt-1.5" data-testid="demo-company" /></div>
              </div>
              <div><Label>Work email</Label><Input value={form.work_email} onChange={set("work_email")} className="mt-1.5" placeholder="you@company.com" data-testid="demo-email" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={form.title} onChange={set("title")} className="mt-1.5" data-testid="demo-title" /></div>
                <div><Label>Team size</Label><Input value={form.team_size} onChange={set("team_size")} className="mt-1.5" placeholder="e.g. 10-50" data-testid="demo-team-size" /></div>
              </div>
              <div><Label>What do you sell?</Label><Input value={form.what_they_sell} onChange={set("what_they_sell")} className="mt-1.5" data-testid="demo-sell" /></div>
              <div><Label>Note (optional)</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" rows={2} data-testid="demo-note" /></div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px]" data-testid="demo-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a demo"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
